import React, { ComponentProps, ReactElement, ReactNode, Ref } from 'react';

import ControlContainer from './ControlContainer';
import Placeholder from './Placeholder';
import { SearchInputProps } from './SearchInput';
import { defaultLabelGetter } from '../../helpers/defaultGetters';
import {
  defaultRenderItems,
  defaultRenderPlaceholder,
} from '../../helpers/defaultRenderers';
import {
  GenericItem,
  GetInputProps,
  GetSelectedItemProps,
  ItemToString,
} from '../../types';

export type MultiControlProps<TItem extends GenericItem> = ComponentProps<
  typeof ControlContainer
> & {
  getInputProps?: GetInputProps;
  getSelectedItemProps: GetSelectedItemProps<TItem>;
  isLoading?: boolean;
  selectedItems: TItem[] | null;
  removeSelectedItem: (item: TItem) => void;
  itemToString?: ItemToString<TItem>;
  placeholder?: string;
  renderInput?: (props: SearchInputProps) => ReactNode;
  renderItems?: ({
    isOpen,
    selectedItems,
    getSelectedItemProps,
    removeSelectedItem,
    itemToString,
    disabled,
  }: {
    getSelectedItemProps: GetSelectedItemProps<TItem>;
    isOpen: boolean;
    selectedItems: TItem[];
    removeSelectedItem: (item: TItem) => void;
    itemToString: ItemToString<TItem>;
    disabled?: boolean;
  }) => ReactNode;
  renderPlaceholder?: (props?: ComponentProps<typeof Placeholder>) => ReactNode;
  disabled?: boolean;
};

const MultiControlWrapped = <TItem extends GenericItem>(
  {
    isOpen = false,
    selectedItems,
    getSelectedItemProps,
    removeSelectedItem,
    getInputProps,
    itemToString = defaultLabelGetter,
    placeholder,
    renderInput: renderInputProp,
    renderItems = defaultRenderItems,
    renderPlaceholder = defaultRenderPlaceholder,
    disabled,
    ...props
  }: MultiControlProps<TItem>,
  ref: Ref<HTMLDivElement | HTMLButtonElement>,
) => {
  const hasSelectedItems = !!selectedItems && !!selectedItems.length;

  const renderInput = () => {
    if (!renderInputProp || !getInputProps) {
      return null;
    }

    const inputProps = getInputProps({
      readOnly: !isOpen,
      disabled,
    });

    return renderInputProp({
      ...inputProps,
      isOpen,
    });
  };

  return (
    <ControlContainer ref={ref} isOpen={isOpen} disabled={disabled} {...props}>
      {hasSelectedItems &&
        renderItems &&
        renderItems({
          isOpen,
          selectedItems,
          getSelectedItemProps,
          removeSelectedItem,
          itemToString,
          disabled,
        })}
      {renderInput()}
      {!hasSelectedItems && renderPlaceholder({ children: placeholder })}
    </ControlContainer>
  );
};

const MultiControl = React.forwardRef(MultiControlWrapped);

MultiControl.displayName = 'MultiControl';

MultiControl.propTypes = {
  ...ControlContainer.propTypes,
};

export default MultiControl as <T extends GenericItem>(
  p: MultiControlProps<T> & { ref?: Ref<HTMLDivElement | HTMLButtonElement> },
) => ReactElement;
